import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Status } from '@df/core/status';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { BulkOrder } from '@df/session/api/basket/bulk-order/bulk-order';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { FileLoadService } from '@df/ui/form/file-upload/file-load.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { csvToJson } from '@df/util/fn/csv-to-json';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import difference from 'lodash-es/difference';
import groupBy from 'lodash-es/groupBy';
import keyBy from 'lodash-es/keyBy';
import mapKeys from 'lodash-es/mapKeys';
import mapValues from 'lodash-es/mapValues';
import pickBy from 'lodash-es/pickBy';
import { Subscription } from 'rxjs';
import { VipService } from '../../module/vip/vip.service';
import { ResultComponent } from '../../ui/resut/result.component';
import { BulkOrderListingComponent } from './bulk-order-listing.component';
import { NBulkOrder } from './bulk-order.interfaces';

@Component({
    selector: 'bulk-order',
    templateUrl: 'bulk-order.html',
    styleUrls: ['bulk-order.scss'],
    standalone: true,
    imports: [SizeClassDirective, ICmsClientModule, ZippyModule, TranslationModule, DfUiModule, ResultComponent, BulkOrderListingComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<BulkOrderComponent>({
    id: NBulkOrder.BULK_BLOCK_URL,
    block: {
        url: NBulkOrder.BULK_BLOCK_URL
    },
    onDemandOnly: true,
    name: 'Bulk orders',
    description: 'Content block for bulk order page',
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-header',
            title: 'Header'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title',
            required: true,
            default: 'Trade quick buy'
        },
        {
            type: NICmsContentField.EFieldType.textarea,
            key: 'intro',
            title: 'Intro',
            required: true,
            default:
                'Etiam nibh facilisi viverra scelerisque leo, purus metus. Eu nulla viverra in ac facilisis libero. Pharetra ac commodo sed vulputate elementum.'
        },
        {
            type: NICmsContentField.EFieldType.textarea,
            key: 'impoterDownloadCopy',
            title: 'Download copy',
            required: true,
            default: 'Download .CSV template'
        }
    ]
})
export class BulkOrderComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NBulkOrder.IBulkOrderBlockContent;

    @ViewChild(BulkOrderListingComponent)
    bulkOrderListingComponent?: BulkOrderListingComponent;
    protected successMsg = '<span class="col-3 inline-flex"><i class="icon-tick p-r-2"></i>File successfully uploaded</span>';
    protected uploadSuccessMsg = 'File successfully uploaded';

    /**
     * Bulko order processor
     */
    readonly bulk = new BulkOrder({ storage: true });

    /**
     * Request service
     */
    private fileLoadService = inject(FileLoadService);

    /**
     * VIP service
     */
    private vipService = inject(VipService);

    /**
     * Import tool status
     */
    readonly importStatus = new Status();

    /**
     * Load file subscription
     */
    private loadSubscription?: Subscription;

    private productResource = inject(ProductResource);

    /**
     * Selected file
     */
    private file?: File;

    /**
     * Percent of load
     */
    importProgress: number | string = 0;

    /**
     * Importer data from file, sanitized
     */
    private csvData?: { sku: string; qty: string }[];

    /**
     * Invalid SKUs
     */
    erroringSkus?: { skus: string[]; error: string }[];

    protected initializing = false;

    /**
     * Read file, sanitize data, notify about problems
     */
    onFileChange(event, autoImport = false) {
        this.file = event.target.files[0];
        event.target.value = '';
        this.csvData = undefined;

        this.cleanupImport();
        this.loadSubscription = this.fileLoadService.load(this.importStatus, this.file!).subscribe(async item => {
            this.importProgress = item.progress === 100 ? 'Processing file' : item.progress;
            this.detectChanges();

            if (item.result) {
                this.cleanupImport();
                try {
                    this.importStatus.busy = true;
                    // parces csv, normalize, validate syntax
                    const data = csvToJson(item.result as string).map(i =>
                        mapKeys(i, (value: string, key: string) => key.toLowerCase())
                    ) as any as {
                        sku: string;
                        qty: string;
                    }[];

                    this.csvData = data;

                    // check if there are any valid rows
                    if (!this.csvData.length) {
                        throw new Error('No records identified, please provide SKU and QTY columns');
                    }

                    if (autoImport) {
                        await this.import();
                    } else {
                        this.importStatus.success = this.uploadSuccessMsg;
                    }
                } catch (err) {
                    this.importStatus.error = err as any;
                }
            }
        });
    }

    /**
     * Does the import
     */
    async import() {
        if (!this.canImport()) {
            return;
        }
        this.detectChanges();
        try {
            this.importStatus.busy = true;

            // load missing products first
            const stringSkuRows = this.csvData!.filter(i => typeof i.sku === 'string' && typeof i.qty === 'string');
            let allProducts = this.productResource.getAllExisting();
            await this.vipService.loadForProducts(allProducts);

            const notLoadedSkus = stringSkuRows.filter(skuRow => !allProducts.find(i => i.data?.sku == skuRow.sku)).map(i => i.sku);
            if (notLoadedSkus.length) {
                // load  products we miss
                const missing = await this.productResource.find({ filter: { sku: { $in: notLoadedSkus } }, verbosity: 2 });
                this.vipService.loadForProducts(missing);
            }

            const duplicatingSkus = Object.keys(pickBy(groupBy(stringSkuRows.map(i => i.sku)), x => x.length > 1));
            allProducts = this.productResource.getAllExisting();

            // test against qties
            const identifiedProducts = stringSkuRows
                .map(row => ({
                    product: allProducts.find(product => product.data?.sku === row.sku),
                    qty: row.qty
                }))
                .filter(i => i.product);

            // SKUs that had no product matched
            const unrecognizedSkus = difference(
                this.csvData!.map(i => i.sku),
                identifiedProducts.map(i => i.product!.data!.sku)
            );

            // test against products
            const validProducts = identifiedProducts
                .filter(i => i.qty.match(/^\d+$/))
                .map(row => ({
                    product: row.product,
                    qty: Number(row.qty)
                }))
                .filter(i => i.product && i.qty > 0);

            const invalidQtySkus = difference(
                identifiedProducts.map(i => i.product!.data!.sku),
                validProducts.map(i => i.product!.data!.sku)
            );

            this.bulk.reset(
                mapValues(
                    keyBy(validProducts, i => i.product!.id),
                    'qty'
                )
            );
            this.bulkOrderListingComponent?.showSelected();

            // bind to error table
            this.erroringSkus = [
                { skus: duplicatingSkus, error: 'The following SKUs appeared more then once:' },
                { skus: unrecognizedSkus, error: 'The following SKUs have not been recognized:' },
                { skus: invalidQtySkus, error: 'The following SKUs have invalid qty value:' }
            ].filter(i => i.skus.length) as { skus: string[]; error: string }[];

            if (this.erroringSkus!.length) {
                this.detectChanges();
                this.importStatus.busy = false;
            } else if (this.bulk.hasErrors()) {
                this.importStatus.error = 'Some products are unavailable in requested quantity';
            } else {
                this.importStatus.success = this.successMsg;
            }
        } catch (err) {
            this.importStatus.error = err as any;
        }
    }

    /**
     * If import can be performed
     */
    canImport(): boolean {
        return !!this.csvData;
    }

    override ngOnDestroy() {
        this.cleanupImport();
        super.ngOnDestroy();
    }

    private cleanupImport() {
        this.erroringSkus = undefined;
        this.loadSubscription?.unsubscribe();
        this.importProgress = 0;
    }

    closeInvalidSkus() {
        this.erroringSkus = undefined;
        this.detectChanges();
    }
}
